/**
 * The rules behind the two lot pages - PlaceBid (a team in a primary auction) and SecodaryPlaceBid
 * (a resale listing) - kept pure so they can be tested on their own (FE-16). The pages share their
 * clock and their close in Player/useLotPage.js; what differs between them is here.
 */

import { withoutClockFields } from './lotClock';

/** Bid/Add refusals (errorCode) after which a lot takes no bid again. A resale refusal has none. */
export const BIDDING_CLOSED_CODES = ['BIDDING_CLOSED', 'BIDDING_COMPLETED', 'BIDDING_CLOSED_BEFORE_CONTEST'];

/**
 * The lot's figures a primary row carries; everything on the page but the clock. #176: with
 * topBidAmount, which the "Start Bid" / "Last Bid" field reads (null while the lot has no bid), and
 * minNextBid.
 */
export const FIGURE_FIELDS = [
    'currentBidAmount',
    'minNextBid',
    'topBidAmount',
    'startingBid',
    'totalPlacedBids',
    'totalPoolAmount',
    'bidPlacedBy',
    'bidPlacedByName',
    'teamName',
];

/**
 * The figures a primary lot's page takes from a row: FIGURE_FIELDS, those the row carries. PlaceBid
 * read them until Auction Details replaced it (#237); useLotPage's tests still drive a team lot
 * with them.
 *
 * @param {object} row A lot row (REST or hub).
 * @returns {object} The figures.
 */
export const pickTeamLotFigures = (row) => {
    const picked = {};
    FIGURE_FIELDS.forEach((field) => {
        if (row?.[field] !== undefined) picked[field] = row[field];
    });
    return picked;
};

/**
 * The figures SecodaryPlaceBid takes from a listing row: every field it carries but the clock
 * (topBidAmount and minNextBid included, #176).
 *
 * @param {object} row A listing row (REST or hub).
 * @returns {object} The figures.
 */
export const pickListingFigures = (row) => {
    const picked = {};
    Object.entries(withoutClockFields(row) || {}).forEach(([field, value]) => {
        if (value !== undefined) picked[field] = value;
    });
    return picked;
};

/**
 * The figures shown, with a row's merged in - the same object when the row changes none, so the
 * Worker's 7-second push does not re-render the page each time.
 *
 * @param {object|null} figures The figures shown.
 * @param {object} picked The row's figures (pickTeamLotFigures or pickListingFigures).
 * @returns {object|null} The new figures, or `figures` itself.
 */
export const mergeFigures = (figures, picked) => {
    const unchanged = Object.keys(picked).every((field) => Object.is(figures?.[field], picked[field]));
    return unchanged ? figures : { ...figures, ...picked };
};

/**
 * Reads SecodaryPlaceBid's reply from SecondaryMarket/SMGetAuctionById (the axios response, or what
 * the wrapper returns on failure).
 *
 * The status is read, not just the body: the wrapper used to return the body alone, so a
 * permission 401 - as empty as a 204 - closed a live listing as lost.
 *
 * @param {object|undefined} res The wrapper's result.
 * @returns {{row: object}|{gone: true}|null} The listing; `gone` for a reply with no listing (204 or
 *   an empty 200), which is a final close (#191); null for no reply or a failure - apiClient has
 *   already said so, and the listing is as it was.
 */
export const readListingReply = (res) => {
    if (!res) return null;
    if (res.status === 204 || (res.status === 200 && !res.data)) return { gone: true };
    if (res.status !== 200 || !res.data?.auctionId) return null;
    return { row: res.data };
};

/**
 * The Bid/Add body, as the old PlaceBid page always sent it and Auction Details sends it (#237): the
 * lot's figures as shown and the auction's bid time. The server takes only the lot and the amount
 * from it (#145).
 *
 * @param {{teamId: number, bidTime: number}} state The team bid on and the auction's bid time.
 * @param {object} figures The figures shown.
 * @param {string} amount The custom amount typed, "" or undefined for the minimum next bid.
 * @param {number|string} userId The player.
 * @returns {Array} The body.
 */
export const teamLotBidBody = (state, figures, amount, userId) => [{
    teamId: Number(state?.teamId),
    auctionId: figures?.auctionId,
    startingBid: figures?.startingBid,
    bidTime: state?.bidTime,
    totalPlacedBids: figures?.totalPlacedBids,
    bidIncrement: figures?.bidIncrement,
    currentBidAmount: Number(figures?.currentBidAmount),
    bidAmount: Number(amount) || figures?.currentBidAmount,
    bidPlacedBy: Number(userId),
}];

/**
 * The listing (AuctionLevel) a resale lot page shows: the Secondary Market row it was opened from.
 *
 * #184 (reopened): the server refuses a resale bid that names no listing - without it there is no
 * telling which round it was meant for - so the page must not send one; null tells it so.
 *
 * @param {object} state The router state (the Secondary Market row).
 * @returns {number|null} The level, a positive integer, or null when the row does not carry one.
 */
export const listingLevel = (state) => {
    const level = Number(state?.auctionLevel);
    return Number.isInteger(level) && level > 0 ? level : null;
};

/**
 * SecodaryPlaceBid's SecondaryMarket/SMPlaceBid body, as it has always been sent. Only for a row
 * with a listing level (listingLevel): the page sends no bid without one.
 *
 * @param {object} state The router state (the Secondary Market row, without its clock).
 * @param {object} figures The figures shown.
 * @param {string} amount The custom amount typed, "" or undefined for the minimum next bid.
 * @returns {Array} The body.
 */
export const listingBidBody = (state, figures, amount) => [{
    teamId: Number(state?.teamId),
    auctionId: state?.auctionId,
    startingBid: state?.startingBid,
    bidTime: state?.bidTime,
    totalPlacedBids: state?.totalPlacedBids,
    bidIncrement: state?.bidIncrement,
    currentBidAmount: Number(figures?.currentBidAmount),
    // With no custom amount, bid the minimum next bid. This used to fall back to startingBid,
    // which on this screen is the current highest bid, and the server only accepted it
    // because it silently raised a bid to the figure the client sent (#145).
    bidAmount: Number(amount) || Number(figures?.currentBidAmount),
    // No ownerShipShare: the share is fixed by the listing, and the server stamps it on the bid
    // itself (usp_InsertSMBidModel).
    // #184: the listing this screen shows (the Secondary Market row it was opened from). The
    // server bids on that listing only and refuses once it has closed; without it the bid went
    // to whichever listing of the team happened to be open - possibly a later one.
    auctionLevel: listingLevel(state),
}];

/**
 * Whether a bid was refused because the player may not bid: unsubscribed or lapsed. The server is
 * the only thing that knows - the subscription gate lives in the bid procedures and exempts
 * practice auctions by auction owner - and both markets word it with this prefix.
 *
 * @param {*} message The refusal's message.
 * @returns {boolean}
 */
export const isSubscriptionRefusal = (message) =>
    typeof message === 'string' && message.startsWith('Bidding is not allowed');
